import { HASURA_CLAIMS_KEY } from 'constants/auth';
import { SIGNUP_PAGE } from 'constants/pages';
import { PastDueNoticeResponse } from 'constants/payloads/dunning';
import { Viewer } from 'constants/user';
import api from 'services/client/api';
import { auth } from 'services/client/firebase';
import { hasAlertAccess, hasDashboardAccess } from 'services/client/token';
import { toRequestError } from './pastDueOffer';

/**
 * The site-wide past-due banner's data: the overdue subscriptions on the signed-in account and
 * the invoice each is waiting on, from `GET /api/v1/my/past-due`.
 *
 * The navbar mounts on every page, so the answer is cached rather than asked again on each
 * navigation. "Nothing is overdue" is the answer for almost every visitor and almost never
 * changes mid-visit, so it is held far longer than an actual overdue notice, whose amount and
 * invoice link do move. The cache is shared across tabs (localStorage, keyed by uid and
 * TTL-bounded), so opening five tabs costs one request rather than five. Anything that could
 * change the answer — paying, updating the card — clears it.
 */
export const PAST_DUE_NOTICE_ENDPOINT = 'my/past-due';

const CACHE_KEY_PREFIX = '@@ifinance/past-due-notice:';
/** Nothing overdue: the common answer, and a subscription does not fail mid-visit very often. */
export const CLEAR_TTL_MS = 30 * 60 * 1000;
/** Something overdue: the banner names an amount and an invoice, both of which can change. */
export const OVERDUE_TTL_MS = 5 * 60 * 1000;

const ttlFor = (notice: PastDueNoticeResponse) =>
  notice.subscriptions.length > 0 ? OVERDUE_TTL_MS : CLEAR_TTL_MS;

interface CachedNotice {
  at: number;
  notice: PastDueNoticeResponse;
}

const cacheKey = (uid: string) => `${CACHE_KEY_PREFIX}${uid}`;

const readCache = (uid: string): PastDueNoticeResponse | null => {
  try {
    const raw = window.localStorage.getItem(cacheKey(uid));
    if (!raw) return null;
    const cached = JSON.parse(raw) as CachedNotice;
    if (
      !cached ||
      typeof cached.at !== 'number' ||
      !Array.isArray(cached.notice?.subscriptions) ||
      Date.now() - cached.at > ttlFor(cached.notice)
    ) {
      return null;
    }
    return cached.notice;
  } catch {
    return null;
  }
};

const writeCache = (uid: string, notice: PastDueNoticeResponse) => {
  try {
    const entry: CachedNotice = { at: Date.now(), notice };
    window.localStorage.setItem(cacheKey(uid), JSON.stringify(entry));
  } catch {
    // Storage can be full or blocked; the banner still works, it just asks again next page.
  }
};

export const clearPastDueNoticeCache = (uid: string) => {
  try {
    window.localStorage.removeItem(cacheKey(uid));
  } catch {
    // Nothing to clear.
  }
};

const isNotice = (value: unknown): value is PastDueNoticeResponse =>
  !!value &&
  typeof value === 'object' &&
  Array.isArray((value as PastDueNoticeResponse).subscriptions);

export const loadPastDueNotice = async (uid: string): Promise<PastDueNoticeResponse> => {
  const cached = readCache(uid);
  if (cached) return cached;

  // `api` injects the bearer token; the route reads nothing else.
  const response = await api.get(PAST_DUE_NOTICE_ENDPOINT);
  const notice: PastDueNoticeResponse = isNotice(response) ? response : { subscriptions: [] };
  writeCache(uid, notice);
  return notice;
};

/**
 * Opens the Stripe customer portal, the same way the Settings page's "Update card" does. Resolves
 * once the browser has been told where to go; rejects with the portal route's message otherwise
 * (the API client rejects a non-2xx response with the route's `{ message, statusCode }` body).
 */
export const openBillingPortal = async () => {
  const token = await auth.currentUser?.getIdToken(true);
  if (!token) {
    window.location.assign(SIGNUP_PAGE);
    return;
  }

  let session: { url?: string };
  try {
    session = (await api.post('payments/stripe/portal', { payload: { token } })) as {
      url?: string;
    };
  } catch (error) {
    throw toRequestError(error);
  }

  if (!session?.url) {
    throw new Error('Could not open the billing portal. Refresh the page and try again.');
  }

  window.location.href = session.url;
};

/**
 * The app reads login claims only at sign-in (the viewer context listens to `onAuthStateChanged`,
 * not to token refreshes), so a customer who had just paid kept the paused view until they logged
 * out and back in, which is the advice the recovered email has had to give. Once the banner sees
 * the renewal paid, this fetches a fresh token and, when its claims grant a plan the page does not
 * have yet, reloads once, the way Login applies a sign-in. The app's invoice webhook writes the
 * claims right after the subscription row, so a short retry covers the gap between the two.
 * Resolves true when it reloaded.
 *
 * Dashboard access includes every plan, so a page that already has it has nothing to gain and
 * is left alone without a refresh (a return from the portal whose full load already read the
 * new claims, say).
 */
export const restoreAccessAfterPayment = async (
  current: Viewer,
  { attempts = 3, delayMs = 4000 }: { attempts?: number; delayMs?: number } = {},
) => {
  if (hasDashboardAccess(current)) return false;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const user = auth.currentUser;
    if (!user) return false;
    try {
      const claims = (await user.getIdTokenResult(true)).claims[HASURA_CLAIMS_KEY];
      if (claims) {
        const next = { ...current, claims } as Viewer;
        const gained =
          (hasDashboardAccess(next) && !hasDashboardAccess(current)) ||
          (hasAlertAccess(next) && !hasAlertAccess(current));
        if (gained) {
          window.location.reload();
          return true;
        }
      }
    } catch {
      // A failed refresh is retried; the next sign-in applies the claims regardless.
    }
    if (attempt < attempts - 1) await new Promise((resolve) => setTimeout(resolve, delayMs));
  }
  return false;
};
